import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { config } from '../config';

interface ApiKey {
  _id: string;
  name: string;
  clientName: string;
  keyId: string;
  keyPreview: string;
  isActive: boolean;
  permissions: {
    canSubmitDaily: boolean;
    canSubmitMissing: boolean;
    canUpdateStatus: boolean;
    canReadData: boolean;
  };
  lastUsed: string | null;
  usageCount: number;
  notes: string;
  createdBy: {
    username: string;
  };
  createdAt: string;
  updatedAt: string;
}

interface ApiKeyStats {
  overview: {
    totalKeys: number;
    activeKeys: number;
    totalUsage: number;
  };
  recentUsage: ApiKey[];
}

interface NewApiKeyData {
  name: string;
  clientName: string;
  permissions: {
    canSubmitDaily: boolean;
    canSubmitMissing: boolean;
    canUpdateStatus: boolean;
    canReadData: boolean;
  };
  notes: string;
}

const ApiKeyManagement: React.FC = () => {
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [stats, setStats] = useState<ApiKeyStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showFullKey, setShowFullKey] = useState<string | null>(null);
  const [editingKey, setEditingKey] = useState<ApiKey | null>(null);
  const [copySuccess, setCopySuccess] = useState<string>('');

  const [newKeyData, setNewKeyData] = useState<NewApiKeyData>({
    name: '',
    clientName: '',
    permissions: {
      canSubmitDaily: true,
      canSubmitMissing: true,
      canUpdateStatus: false,
      canReadData: true
    },
    notes: ''
  });

  useEffect(() => {
    loadApiKeys();
    loadStats();
  }, []);

  const loadApiKeys = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${config.API_BASE_URL}/keys`);
      setApiKeys(response.data.data || []);
    } catch (error: any) {
      setError('Failed to load API keys');
      console.error('Error loading API keys:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await axios.get(`${config.API_BASE_URL}/keys/stats`);
      setStats(response.data.data);
    } catch (error: any) {
      console.error('Error loading stats:', error);
    }
  };

  const createApiKey = async () => {
    try {
      const response = await axios.post(`${config.API_BASE_URL}/keys`, newKeyData);
      
      if (response.data.success) {
        setShowFullKey(response.data.data.fullKey);
        setShowCreateModal(false);
        resetNewKeyData();
        loadApiKeys();
        loadStats();
      }
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to create API key');
    }
  };

  const updateApiKey = async (id: string, updates: Partial<ApiKey>) => {
    try {
      await axios.put(`${config.API_BASE_URL}/keys/${id}`, updates);
      setEditingKey(null);
      loadApiKeys();
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to update API key');
    }
  };

  const regenerateApiKey = async (id: string) => {
    if (!window.confirm('Are you sure you want to regenerate this API key? The old key will stop working immediately.')) {
      return;
    }

    try {
      const response = await axios.post(`${config.API_BASE_URL}/keys/${id}/regenerate`);
      if (response.data.success) {
        setShowFullKey(response.data.data.fullKey);
        loadApiKeys();
        loadStats();
      }
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to regenerate API key');
    }
  };

  const deleteApiKey = async (id: string, name: string) => {
    if (!window.confirm(`Are you sure you want to delete the API key "${name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await axios.delete(`${config.API_BASE_URL}/keys/${id}`);
      loadApiKeys();
      loadStats();
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to delete API key');
    }
  };

  const resetNewKeyData = () => {
    setNewKeyData({
      name: '',
      clientName: '',
      permissions: {
        canSubmitDaily: true,
        canSubmitMissing: true,
        canUpdateStatus: false,
        canReadData: true
      },
      notes: ''
    });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopySuccess('API key copied to clipboard!');
      setTimeout(() => setCopySuccess(''), 3000);
    } catch (err) {
      setCopySuccess('Failed to copy to clipboard');
      setTimeout(() => setCopySuccess(''), 3000);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-4">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div>
      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          <i className="bi bi-exclamation-triangle me-2"></i>
          {error}
          <button type="button" className="btn-close" onClick={() => setError('')}></button>
        </div>
      )}

      {copySuccess && (
        <div className="alert alert-success alert-dismissible fade show" role="alert">
          <i className="bi bi-check-circle me-2"></i>
          {copySuccess}
          <button type="button" className="btn-close" onClick={() => setCopySuccess('')}></button>
        </div>
      )}

      {/* Statistics Overview */}
      {stats && (
        <div className="row mb-4">
          <div className="col-md-4">
            <div className="card bg-primary text-white">
              <div className="card-body">
                <div className="d-flex justify-content-between">
                  <div>
                    <h6>Total API Keys</h6>
                    <h4>{stats.overview.totalKeys}</h4>
                  </div>
                  <div className="align-self-center">
                    <i className="bi bi-key fs-1"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-4">
            <div className="card bg-success text-white">
              <div className="card-body">
                <div className="d-flex justify-content-between">
                  <div>
                    <h6>Active Keys</h6>
                    <h4>{stats.overview.activeKeys}</h4>
                  </div>
                  <div className="align-self-center">
                    <i className="bi bi-check-circle fs-1"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-4">
            <div className="card bg-info text-white">
              <div className="card-body">
                <div className="d-flex justify-content-between">
                  <div>
                    <h6>Total API Calls</h6>
                    <h4>{stats.overview.totalUsage.toLocaleString()}</h4>
                  </div>
                  <div className="align-self-center">
                    <i className="bi bi-graph-up fs-1"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="d-flex justify-content-between mb-4">
        <h5 className="mb-0">
          <i className="bi bi-key me-2"></i>
          API Key Management
        </h5>
        <button
          className="btn btn-primary"
          onClick={() => setShowCreateModal(true)}
        >
          <i className="bi bi-plus-circle me-1"></i>
          Create New API Key
        </button>
      </div>

      {/* API Keys Table */}
      <div className="card">
        <div className="card-body">
          <div className="table-responsive">
            <table className="table table-hover">
              <thead className="table-dark">
                <tr>
                  <th>Name</th>
                  <th>Client</th>
                  <th>Key Preview</th>
                  <th>Permissions</th>
                  <th>Usage</th>
                  <th>Last Used</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {apiKeys.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-center py-4">
                      <i className="bi bi-key fs-1 text-muted"></i>
                      <p className="text-muted mt-2">No API keys found</p>
                    </td>
                  </tr>
                ) : (
                  apiKeys.map((key) => (
                    <tr key={key._id}>
                      <td>
                        <strong>{key.name}</strong>
                        {key.notes && (
                          <small className="d-block text-muted">{key.notes}</small>
                        )}
                      </td>
                      <td>{key.clientName}</td>
                      <td>
                        <code className="text-muted">{key.keyPreview}</code>
                      </td>
                      <td>
                        <div className="d-flex flex-wrap gap-1">
                          {key.permissions.canSubmitDaily && (
                            <span className="badge bg-primary">Daily</span>
                          )}
                          {key.permissions.canSubmitMissing && (
                            <span className="badge bg-secondary">Missing</span>
                          )}
                          {key.permissions.canUpdateStatus && (
                            <span className="badge bg-warning">Status</span>
                          )}
                          {key.permissions.canReadData && (
                            <span className="badge bg-info">Read</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <span className="badge bg-light text-dark">
                          {key.usageCount.toLocaleString()}
                        </span>
                      </td>
                      <td>
                        {key.lastUsed ? (
                          <small className="text-muted">
                            {formatDate(key.lastUsed)}
                          </small>
                        ) : (
                          <small className="text-muted">Never</small>
                        )}
                      </td>
                      <td>
                        {key.isActive ? (
                          <span className="badge bg-success">Active</span>
                        ) : (
                          <span className="badge bg-danger">Inactive</span>
                        )}
                      </td>
                      <td>
                        <div className="btn-group btn-group-sm">
                          <button
                            className="btn btn-outline-primary"
                            onClick={() => setEditingKey(key)}
                            title="Edit"
                          >
                            <i className="bi bi-pencil"></i>
                          </button>
                          <button
                            className="btn btn-outline-warning"
                            onClick={() => regenerateApiKey(key._id)}
                            title="Regenerate"
                          >
                            <i className="bi bi-arrow-clockwise"></i>
                          </button>
                          <button
                            className="btn btn-outline-danger"
                            onClick={() => deleteApiKey(key._id, key.name)}
                            title="Delete"
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Create API Key Modal */}
      {showCreateModal && (
        <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-plus-circle me-2"></i>
                  Create New API Key
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowCreateModal(false);
                    resetNewKeyData();
                  }}
                ></button>
              </div>
              <div className="modal-body">
                <form>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Key Name *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={newKeyData.name}
                        onChange={(e) => setNewKeyData({...newKeyData, name: e.target.value})}
                        placeholder="e.g., Production API Key"
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Client Name *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={newKeyData.clientName}
                        onChange={(e) => setNewKeyData({...newKeyData, clientName: e.target.value})}
                        placeholder="e.g., Acme Corporation"
                      />
                    </div>
                  </div>
                  
                  <div className="mb-3">
                    <label className="form-label">Permissions</label>
                    <div className="row">
                      <div className="col-md-6">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            checked={newKeyData.permissions.canSubmitDaily}
                            onChange={(e) => setNewKeyData({
                              ...newKeyData,
                              permissions: {...newKeyData.permissions, canSubmitDaily: e.target.checked}
                            })}
                          />
                          <label className="form-check-label">
                            <strong>Submit Daily Data</strong>
                            <small className="d-block text-muted">Replace all existing data</small>
                          </label>
                        </div>
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            checked={newKeyData.permissions.canSubmitMissing}
                            onChange={(e) => setNewKeyData({
                              ...newKeyData,
                              permissions: {...newKeyData.permissions, canSubmitMissing: e.target.checked}
                            })}
                          />
                          <label className="form-check-label">
                            <strong>Submit Missing Data</strong>
                            <small className="d-block text-muted">Add new records to existing data</small>
                          </label>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            checked={newKeyData.permissions.canUpdateStatus}
                            onChange={(e) => setNewKeyData({
                              ...newKeyData,
                              permissions: {...newKeyData.permissions, canUpdateStatus: e.target.checked}
                            })}
                          />
                          <label className="form-check-label">
                            <strong>Update Status</strong>
                            <small className="d-block text-muted">Modify delivery completion status</small>
                          </label>
                        </div>
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            checked={newKeyData.permissions.canReadData}
                            onChange={(e) => setNewKeyData({
                              ...newKeyData,
                              permissions: {...newKeyData.permissions, canReadData: e.target.checked}
                            })}
                          />
                          <label className="form-check-label">
                            <strong>Read Data</strong>
                            <small className="d-block text-muted">Retrieve purchase order information</small>
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Notes (Optional)</label>
                    <textarea
                      className="form-control"
                      rows={3}
                      value={newKeyData.notes}
                      onChange={(e) => setNewKeyData({...newKeyData, notes: e.target.value})}
                      placeholder="Additional notes about this API key..."
                    />
                  </div>
                </form>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowCreateModal(false);
                    resetNewKeyData();
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={createApiKey}
                  disabled={!newKeyData.name || !newKeyData.clientName}
                >
                  <i className="bi bi-plus-circle me-1"></i>
                  Create API Key
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Show Full API Key Modal */}
      {showFullKey && (
        <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header bg-warning">
                <h5 className="modal-title">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  API Key Created Successfully
                </h5>
              </div>
              <div className="modal-body">
                <div className="alert alert-warning">
                  <strong>Important:</strong> This is the only time you'll see the full API key. 
                  Copy it now and store it securely.
                </div>
                
                <label className="form-label">Your new API key:</label>
                <div className="input-group">
                  <input
                    type="text"
                    className="form-control font-monospace"
                    value={showFullKey}
                    readOnly
                  />
                  <button
                    className="btn btn-outline-secondary"
                    type="button"
                    onClick={() => copyToClipboard(showFullKey)}
                    title="Copy to clipboard"
                  >
                    <i className="bi bi-clipboard"></i>
                  </button>
                </div>
                
                <div className="mt-3">
                  <small className="text-muted">
                    <strong>Usage:</strong> Include this key in your API requests using the 
                    <code>X-API-Key</code> header or <code>Authorization: Bearer</code> header.
                  </small>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => setShowFullKey(null)}
                >
                  I've Copied the Key
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit API Key Modal */}
      {editingKey && (
        <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-pencil me-2"></i>
                  Edit API Key: {editingKey.name}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setEditingKey(null)}
                ></button>
              </div>
              <div className="modal-body">
                <form>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Key Name</label>
                      <input
                        type="text"
                        className="form-control"
                        value={editingKey.name}
                        onChange={(e) => setEditingKey({...editingKey, name: e.target.value})}
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Client Name</label>
                      <input
                        type="text"
                        className="form-control"
                        value={editingKey.clientName}
                        onChange={(e) => setEditingKey({...editingKey, clientName: e.target.value})}
                      />
                    </div>
                  </div>

                  <div className="mb-3">
                    <div className="form-check form-switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={editingKey.isActive}
                        onChange={(e) => setEditingKey({...editingKey, isActive: e.target.checked})}
                      />
                      <label className="form-check-label">
                        <strong>Active</strong>
                        <small className="d-block text-muted">Inactive keys cannot be used for API calls</small>
                      </label>
                    </div>
                  </div>
                  
                  <div className="mb-3">
                    <label className="form-label">Permissions</label>
                    <div className="row">
                      <div className="col-md-6">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            checked={editingKey.permissions.canSubmitDaily}
                            onChange={(e) => setEditingKey({
                              ...editingKey,
                              permissions: {...editingKey.permissions, canSubmitDaily: e.target.checked}
                            })}
                          />
                          <label className="form-check-label">Submit Daily Data</label>
                        </div>
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            checked={editingKey.permissions.canSubmitMissing}
                            onChange={(e) => setEditingKey({
                              ...editingKey,
                              permissions: {...editingKey.permissions, canSubmitMissing: e.target.checked}
                            })}
                          />
                          <label className="form-check-label">Submit Missing Data</label>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            checked={editingKey.permissions.canUpdateStatus}
                            onChange={(e) => setEditingKey({
                              ...editingKey,
                              permissions: {...editingKey.permissions, canUpdateStatus: e.target.checked}
                            })}
                          />
                          <label className="form-check-label">Update Status</label>
                        </div>
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            checked={editingKey.permissions.canReadData}
                            onChange={(e) => setEditingKey({
                              ...editingKey,
                              permissions: {...editingKey.permissions, canReadData: e.target.checked}
                            })}
                          />
                          <label className="form-check-label">Read Data</label>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Notes</label>
                    <textarea
                      className="form-control"
                      rows={3}
                      value={editingKey.notes}
                      onChange={(e) => setEditingKey({...editingKey, notes: e.target.value})}
                    />
                  </div>
                </form>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setEditingKey(null)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => updateApiKey(editingKey._id, editingKey)}
                >
                  <i className="bi bi-check-circle me-1"></i>
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApiKeyManagement; 